import React from 'react';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Trash, X as XIcon, Flag, ListChecks, User as UserIcon, Calendar, Lightning } from '@phosphor-icons/react';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { PriorityIcon } from './PriorityIcon';

const STATUSES = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Sticky bulk-actions bar — shows when 1+ tasks are selected.
// Mirrors Jira/Asana bulk-edit bar: status/priority/assignee/sprint/delete.
export default function BulkActionsBar({
  selectedIds = [],
  onClear,
  onApplyUpdate,   // async (updates) => void
  onApplyDelete,   // async () => void
  members = [],
  customStatuses = [],
  sprints = [],
  busy = false,
}) {
  const count = selectedIds.length;
  if (count === 0) return null;

  const allStatuses = [
    ...STATUSES,
    ...((customStatuses || []).map(s => ({ value: s.id, label: s.label }))),
  ];

  return (
    <div
      data-testid="bulk-actions-bar"
      className="sticky top-0 z-30 mx-4 mt-3 mb-1 px-4 py-2 rounded-lg border border-blue-200 bg-blue-50/90 backdrop-blur shadow-sm flex items-center gap-2"
    >
      <ListChecks size={16} className="text-blue-700 flex-shrink-0" />
      <span className="text-[12px] font-bold text-blue-900" data-testid="bulk-count">
        {count} selected
      </span>
      <span className="text-blue-200">·</span>

      {/* Status */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-700 hover:bg-white" disabled={busy} data-testid="bulk-status-trigger">
            <Lightning size={12} className="mr-1" /> Status
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Set status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {allStatuses.map(s => (
            <DropdownMenuItem key={s.value} onClick={() => onApplyUpdate({ status: s.value })} data-testid={`bulk-status-${s.value}`}>
              {s.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Priority */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-700 hover:bg-white" disabled={busy} data-testid="bulk-priority-trigger">
            <Flag size={12} className="mr-1" /> Priority
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-40">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Set priority</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {PRIORITIES.map(p => (
            <DropdownMenuItem key={p.value} onClick={() => onApplyUpdate({ priority: p.value })} data-testid={`bulk-priority-${p.value}`}>
              <PriorityIcon priority={p.value} /> <span className="ml-2">{p.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Assignee */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-700 hover:bg-white" disabled={busy} data-testid="bulk-assignee-trigger">
            <UserIcon size={12} className="mr-1" /> Assignee
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-auto">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Assign to</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onApplyUpdate({ assignee_id: null })} data-testid="bulk-assignee-unassigned">
            <span className="text-zinc-500">Unassigned</span>
          </DropdownMenuItem>
          {(members || []).map(m => (
            <DropdownMenuItem key={m.user_id} onClick={() => onApplyUpdate({ assignee_id: m.user_id })} data-testid={`bulk-assignee-${m.user_id}`}>
              <Avatar className="h-5 w-5 mr-2 flex-shrink-0">
                <AvatarImage src={m.avatar} />
                <AvatarFallback className={`${getAvatarStyle(m.user_id || m.name).bg} text-white text-[9px] font-bold`}>{getInitials(m.name)}</AvatarFallback>
              </Avatar>
              <span className="truncate">{m.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Sprint */}
      {(sprints?.length > 0) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-700 hover:bg-white" disabled={busy} data-testid="bulk-sprint-trigger">
              <Calendar size={12} className="mr-1" /> Sprint
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Move to sprint</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onApplyUpdate({ sprint_id: null })} data-testid="bulk-sprint-backlog">Backlog</DropdownMenuItem>
            {sprints.filter(s => s.status !== 'completed').map(s => (
              <DropdownMenuItem key={s.sprint_id} onClick={() => onApplyUpdate({ sprint_id: s.sprint_id })} data-testid={`bulk-sprint-${s.sprint_id}`}>
                {s.name}{s.status === 'active' ? ' · Active' : ''}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <span className="flex-1" />

      <Button size="sm" variant="ghost" onClick={onApplyDelete} disabled={busy}
        className="h-7 text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700"
        data-testid="bulk-delete-btn">
        <Trash size={12} className="mr-1" /> Delete
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear} disabled={busy}
        className="h-7 text-[11px] text-zinc-500 hover:bg-zinc-200/60 hover:text-zinc-800"
        data-testid="bulk-clear-btn">
        <XIcon size={12} className="mr-1" /> Clear
      </Button>
    </div>
  );
}
